import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import type { UUID } from '@/index'

type SessionOpenFn = () => void

interface SessionDialogCommands {
  readonly registerSession: (sessionId: UUID, openFn: SessionOpenFn) => void
  readonly unregisterSession: (sessionId: UUID) => void
  readonly openSessionEvent: (sessionId: UUID) => void
  readonly registerSessionDrafts: (sessionId: UUID, openFn: SessionOpenFn) => void
  readonly unregisterSessionDrafts: (sessionId: UUID) => void
  readonly openSessionDrafts: (sessionId: UUID) => void
}

const SESSIONS_HOME_ROUTE = '/hosts'

// Commands carry only stable callbacks, so consumers (every mounted
// SessionEventsOrchestrator, BlockingInbox) never re-render on inbox clicks.
const SessionDialogCommandsContext = createContext<SessionDialogCommands | null>(null)

// The reactive pending target lives in its own context so only the subscriber
// that actually needs it (EventsPage) re-renders when it changes.
const PendingSessionTargetContext = createContext<UUID | null>(null)

interface SessionDialogManagerProviderProps {
  readonly children: ReactNode
}

export const SessionDialogManagerProvider: FC<SessionDialogManagerProviderProps> = ({
  children,
}) => {
  const openFnsRef = useRef<Map<UUID, SessionOpenFn>>(new Map())
  const draftFnsRef = useRef<Map<UUID, SessionOpenFn>>(new Map())
  const pendingTargetRef = useRef<UUID | null>(null)
  const [pendingTarget, setPendingTarget] = useState<UUID | null>(null)
  const navigate = useNavigate()

  // Keep the ref in sync synchronously so registerSession (called on a later
  // orchestrator mount) reads the latest target without waiting for re-render.
  const updatePendingTarget = useCallback((id: UUID | null): void => {
    pendingTargetRef.current = id
    setPendingTarget(id)
  }, [])

  const registerSession = useCallback(
    (sessionId: UUID, openFn: SessionOpenFn): void => {
      openFnsRef.current.set(sessionId, openFn)

      if (sessionId === pendingTargetRef.current) {
        openFn()
        updatePendingTarget(null)
      }
    },
    [updatePendingTarget],
  )

  const unregisterSession = useCallback((sessionId: UUID): void => {
    openFnsRef.current.delete(sessionId)
  }, [])

  const openSessionEvent = useCallback(
    (sessionId: UUID): void => {
      const openFn = openFnsRef.current.get(sessionId)
      if (openFn) {
        openFn()
        return
      }

      updatePendingTarget(sessionId)
      navigate(SESSIONS_HOME_ROUTE)
    },
    [navigate, updatePendingTarget],
  )

  const registerSessionDrafts = useCallback((sessionId: UUID, openFn: SessionOpenFn): void => {
    draftFnsRef.current.set(sessionId, openFn)
  }, [])

  const unregisterSessionDrafts = useCallback((sessionId: UUID): void => {
    draftFnsRef.current.delete(sessionId)
  }, [])

  // No pending-target/navigate fallback: drafts only matter while the
  // orchestrator is mounted, so a missing entry is simply a no-op.
  const openSessionDrafts = useCallback((sessionId: UUID): void => {
    const openFn = draftFnsRef.current.get(sessionId)
    openFn?.()
  }, [])

  const commands = useMemo<SessionDialogCommands>(
    () => ({
      registerSession,
      unregisterSession,
      openSessionEvent,
      registerSessionDrafts,
      unregisterSessionDrafts,
      openSessionDrafts,
    }),
    [
      registerSession,
      unregisterSession,
      openSessionEvent,
      registerSessionDrafts,
      unregisterSessionDrafts,
      openSessionDrafts,
    ],
  )

  return (
    <SessionDialogCommandsContext.Provider value={commands}>
      <PendingSessionTargetContext.Provider value={pendingTarget}>
        {children}
      </PendingSessionTargetContext.Provider>
    </SessionDialogCommandsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSessionDialogManager(): SessionDialogCommands {
  const context = useContext(SessionDialogCommandsContext)
  if (!context) {
    throw new Error('useSessionDialogManager must be used within SessionDialogManagerProvider')
  }
  return context
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePendingSessionTarget(): UUID | null {
  return useContext(PendingSessionTargetContext)
}
